import { theme } from '../../theme';
import { Box } from '../box/box';

type PasswordRule = { label: string; ok: boolean };

// Visual only: the yup schema still decides whether the form is valid.
export const passwordRules = (password: string): PasswordRule[] => [
  { label: 'At least 8 characters', ok: password.length >= 8 },
  { label: 'One uppercase letter', ok: /[A-Z]/.test(password) },
  { label: 'One lowercase letter', ok: /[a-z]/.test(password) },
  { label: 'One number', ok: /[0-9]/.test(password) },
  { label: 'One special character', ok: /[^\w\s]/.test(password) },
];

export const PasswordRules = ({ rules }: { rules: PasswordRule[] }) => (
  <Box
    component="ul"
    aria-live="polite"
    sx={{
      listStyle: 'none',
      m: '8px 0 0',
      p: 0,
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px 14px',
    }}
  >
    {rules.map(({ label, ok }) => (
      <Box
        component="li"
        key={label}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          fontSize: '12px',
          color: ok
            ? theme.custom?.stepDoneText
            : theme.custom?.tableHeaderText,
        }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: ok ? theme.custom?.stepDoneBg : 'transparent',
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            sx={{ width: 12, height: 12 }}
          >
            {ok ? (
              <path
                d="m5 12.5 4.5 4.5L19 7.5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <circle cx="12" cy="12" r="3.2" fill="currentColor" />
            )}
          </Box>
        </Box>
        <span>
          {label}
          <Box component="span" sx={visuallyHidden}>
            {ok ? ' (met)' : ' (not met)'}
          </Box>
        </span>
      </Box>
    ))}
  </Box>
);

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;
